if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

      }
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;

        const variantIdInput = this.form.querySelector('[name=id]');
        if (variantIdInput) variantIdInput.disabled = false;
        this.addSkuPropertyIfMissing();
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
        this.hideErrors = this.dataset.hideErrors === 'true';

        // The server renders this button's disabled/sold-out state against
        // product.selected_or_first_available_variant, which isn't what's
        // actually selected while a required option (e.g. a dropdown picker's
        // blank "Select Size") is still unset. Reset it until the customer
        // picks something; the required-selection check on submit, and the
        // variant-selects re-fetch once a full combination is chosen, take it
        // from there.
        if (this.dataset.soldOutRegardlessOfSelection !== 'true' && this.getUnselectedRequiredVariantSelect()) {
          this.submitButton.disabled = false;
          this.submitButton.removeAttribute('aria-disabled');
          const label = this.submitButton.querySelector('span');
          if (label && window.variantStrings) label.textContent = window.variantStrings.addToCart;
        }
      }

      addSkuPropertyIfMissing() {
        if (!this.form) return;

        const labelSkuPair = this.dataset.labelSkuPair;
        const labelSku = this.dataset.labelSku;
        const propertyLabel = labelSkuPair || labelSku || 'SKU';
        const propertyName = `properties[${propertyLabel}]`;

        if (this.form.querySelector(`input[name="${propertyName}"]`)) return;

        const card = this.closest('.ss__result, .product-card-wrapper, .card-wrapper');
        if (!card) return;

        const skuElement = card.querySelector('.card-information__sku-value, .card-information__sku .caption-with-letter-spacing');
        if (!skuElement || !skuElement.textContent) return;

        const sku = skuElement.textContent.trim();
        if (!sku) return;

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = propertyName;
        input.value = sku;
        this.form.appendChild(input);
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        const requiredVariantSelect = this.getUnselectedRequiredVariantSelect();
        if (requiredVariantSelect) {
          this.handleErrorMessage(this.getRequiredSelectionMessage(requiredVariantSelect));
          requiredVariantSelect.focus();
          return;
        }

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        let quantityInput =
          this.closest('.main-product__row-sticky-wrapper')?.querySelector('.quantity__input') ||
          this.closest('.main-product__row-buttons')?.querySelector('.quantity__input') ||
          this.closest('product-info')?.querySelector('.quantity__input');

        if (quantityInput && quantityInput.value) {
          formData.set('quantity', quantityInput.value);
        }

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response 
              });
              this.cart.renderContents(response);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButton.querySelector('span').classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id'), cartData: response });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading-overlay__spinner').classList.add('hidden');
          });
      }

      getUnselectedRequiredVariantSelect() {
        const sectionId = this.dataset.sectionId;
        const variantPicker = sectionId
          ? document.querySelector(`variant-selects[data-section="${sectionId}"]`)
          : null;
        const requiredVariantSelects = variantPicker
          ? variantPicker.querySelectorAll('select[data-required-option="true"]')
          : Array.from(this.form.elements).filter(
              (element) => element instanceof HTMLSelectElement && element.dataset.requiredOption === 'true'
            );

        return Array.from(requiredVariantSelects).find((selectElement) => !selectElement.value);
      }

      getRequiredSelectionMessage(selectElement) {
        const optionNameMatch = selectElement.name.match(/options\[([^\]]*)\]/);
        const optionName = selectElement.dataset.optionName || (optionNameMatch ? optionNameMatch[1] : '');

        if (!optionName) return window.variantStrings.selectOptionFirstFallback;
        if (optionName.toLowerCase() === 'size') return window.variantStrings.selectSizeFirst;
        return window.variantStrings.selectOptionFirst.replace('[option_name]', optionName.toLowerCase());
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper ??=
          this.closest('.main-product__buy-bar, .main-product__row-buttons--sticky, .quick-add')
            ?.querySelector(':scope > .product-form__error-message-wrapper') ??
          this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;

        this.errorMessage ??= this.errorMessageWrapper.querySelector('.product-form__error-message');
        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);
        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }
    }
  );
}
